import {
  BasePromotion,
  BasePromotionRewardProduct,
  BasePromotionRewardProductItem,
} from '@/types/http/promotion/common';
import { devtools } from 'zustand/middleware';
import { createWithEqualityFn } from 'zustand/traditional';

export type BaseFreeGift = {
  promotion_id?: string;
  product_group: BasePromotionRewardProduct;
  items: (BasePromotionRewardProductItem & {
    qty?: number;
  })[];
};

type TPromotion = {
  selectedPromotion: BasePromotion[];
  selectedFreeGift: BaseFreeGift[];
  setSelectedPromotion: (promotion: BasePromotion[]) => void;
  removePromotion: (promoId: string) => void;
  addPromotion: (promo: BasePromotion, grandTotal: number, listPromotion?: BasePromotion[]) => void;
  applyPromotion: (promoToApply: BasePromotion | null) => void;
  applyVoucher: (voucherIds: string[], listPromotion: BasePromotion[]) => void;
  applyVoucherRewards: (
    promoRewards: BasePromotion[],
    voucherCode: string,
    isNewOverlapFalse: boolean
  ) => void;
  replacePromotion: (newPromo: BasePromotion) => void;
  removeFreeGift: (promoId: string) => void;
  syncFreeGiftPromotion: () => void;
  replaceFreeGift: (promoRewardId: string, data: BaseFreeGift[]) => void;
  removeFreeGiftByItemId: (promoId: string, itemId: string) => void;
  totalDiscount: number;
  setTotalDiscount: (total: number) => void;
  clearSelectedPromotions: () => void;
};

export const usePromotionStore = createWithEqualityFn<TPromotion>()(
  devtools((set) => ({
    selectedPromotion: [],
    selectedFreeGift: [],
    setSelectedPromotion: (promotion) => set({ selectedPromotion: promotion }),
    addPromotion: (promo, grandTotal, listPromotion) =>
      set((state) => {
        let promosTiering = state.selectedPromotion.filter(
          (p): p is BasePromotion => p !== undefined && p.promotion_tier_id !== null
        );

        const promosNonTiering = state.selectedPromotion.filter(
          (p): p is BasePromotion =>
            p !== undefined && p.promotion_tier_id === null && p.minimum_spending <= grandTotal
        );

        if (listPromotion) {
          promosTiering = promosTiering
            .map((selected) => {
              const eligibleTier = listPromotion
                .filter(
                  (p) =>
                    p.promotion_id === selected.promotion_id && p.minimum_spending <= grandTotal
                )
                .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];
              return eligibleTier || selected;
            })
            .filter((p): p is BasePromotion => p !== undefined);
        }

        let promoToAdd: BasePromotion | undefined = promo;
        if (listPromotion) {
          const eligiblePromo = listPromotion
            .filter(
              (p) => p.promotion_id === promo.promotion_id && p.minimum_spending <= grandTotal
            )
            .sort((a, b) => b.minimum_spending - a.minimum_spending)[0];
          if (eligiblePromo) promoToAdd = eligiblePromo;
          else promoToAdd = undefined;
        } else if (promo.minimum_spending > grandTotal) {
          promoToAdd = undefined;
        }

        let selectedPromotion = [...promosNonTiering, ...promosTiering];
        if (promoToAdd) {
          selectedPromotion = selectedPromotion.filter(
            (p) => p.promotion_id !== promoToAdd!.promotion_id
          );
          selectedPromotion.push(promoToAdd);
        }

        selectedPromotion = selectedPromotion.filter((p) => p.minimum_spending <= grandTotal);

        const selectedFreeGift = state.selectedFreeGift.filter((gift) => {
          const matchedPromo = selectedPromotion.find((p) => p.promotion_id === gift.promotion_id);

          if (!matchedPromo || !matchedPromo.product_reward?.length) return false;

          const validProductGroupIds = matchedPromo.product_reward.flatMap((reward) =>
            reward.reward_items.map((item) => item.target_id)
          );

          return validProductGroupIds.includes(gift.product_group.product_group_id);
        });

        return {
          selectedPromotion,
          selectedFreeGift,
        };
      }),
    removePromotion: (promoId: string) =>
      set((state) => ({
        selectedPromotion: state.selectedPromotion.filter(
          (promo) => promo.promotion_id !== promoId
        ),
      })),
    applyPromotion: (promoToApply: BasePromotion | null) =>
      set((state) => {
        if (!promoToApply) return { selectedPromotion: state.selectedPromotion };

        const hasSelectedNonOverlap = state.selectedPromotion.some(
          (promo) => promo.is_overlap === false
        );
        const voucherPromos = state.selectedPromotion.filter((promo) => promo.use_voucher === true);

        const filteredFreeGift = state.selectedFreeGift.filter(
          (gift) => gift.promotion_id !== promoToApply.promotion_id
        );

        if (promoToApply.is_overlap === false || hasSelectedNonOverlap) {
          return { selectedPromotion: [promoToApply], selectedFreeGift: filteredFreeGift };
        }
        const alreadyAdded = voucherPromos.some(
          (promo) => promo.promotion_id === promoToApply.promotion_id
        );

        if (alreadyAdded)
          return { selectedPromotion: voucherPromos, selectedFreeGift: filteredFreeGift };

        return {
          selectedPromotion: [...voucherPromos, promoToApply],
          selectedFreeGift: filteredFreeGift,
        };
      }),
    applyVoucher: (voucherIds, listPromotion) =>
      set(() => {
        const selectedPromotions = listPromotion.filter((promo) =>
          voucherIds.includes(promo.promotion_id)
        );
        const selectingNonOverlap = selectedPromotions.some((promo) => promo.is_overlap === false);

        if (selectingNonOverlap) {
          return { selectedPromotion: selectedPromotions };
        }

        const overlapSelected = selectedPromotions.filter((p) => p.is_overlap === true);
        return { selectedPromotion: overlapSelected };
      }),
    applyVoucherRewards: (
      promoRewards: BasePromotion[],
      voucherCode: string,
      isNewOverlapFalse: boolean
    ) =>
      set((state) => {
        const prevFiltered = state.selectedPromotion.filter(
          (p): p is BasePromotion => p !== undefined
        );
        const hasPrevNonOverlap = prevFiltered.some((p) => p.is_overlap === false);

        const promoRewardsWithVoucher = promoRewards.map((promo) => ({
          ...promo,
          voucher_code: voucherCode,
        }));

        if (isNewOverlapFalse || hasPrevNonOverlap) {
          return { selectedPromotion: promoRewardsWithVoucher };
        }

        const merged = [...prevFiltered];
        promoRewardsWithVoucher.forEach((newPromo) => {
          const exists = merged.some((p) => p.promotion_id === newPromo.promotion_id);
          if (!exists) merged.push(newPromo);
        });

        return { selectedPromotion: merged };
      }),
    replacePromotion: (newPromo: BasePromotion) =>
      set((state) => {
        const filtered = state.selectedPromotion.filter(
          (p) => p.promotion_id !== newPromo.promotion_id
        );
        return {
          selectedPromotion: [...filtered, newPromo],
          selectedFreeGift: state.selectedFreeGift.filter(
            (gift) => gift.promotion_id !== newPromo.promotion_id
          ),
        };
      }),
    removeFreeGift: (promotionId: string) =>
      set((state) => ({
        selectedFreeGift: state.selectedFreeGift.filter(
          (gift) => gift.promotion_id !== promotionId
        ),
      })),
    syncFreeGiftPromotion: () =>
      set((state) => {
        const filtered = state.selectedFreeGift.filter((gift) =>
          state.selectedPromotion.some((promo) => promo.promotion_id === gift.promotion_id)
        );

        const isEqual =
          filtered.length === state.selectedFreeGift.length &&
          filtered.every(
            (gift, idx) => gift.promotion_id === state.selectedFreeGift[idx].promotion_id
          );

        if (isEqual) return {};

        return {
          selectedFreeGift: filtered,
        };
      }),
    replaceFreeGift: (promoRewardId: string, data: BaseFreeGift[]) =>
      set((state) => {
        const filtered = state.selectedFreeGift.filter(
          (item) => item.promotion_id !== promoRewardId
        );
        return {
          selectedFreeGift: [...filtered, ...data],
        };
      }),
    removeFreeGiftByItemId: (promoId: string, itemId: string) =>
      set((state) => {
        const updatedFreeGifts = state.selectedFreeGift
          .map((gift) => {
            if (gift.promotion_id !== promoId) return gift;
            return {
              ...gift,
              items: gift.items.filter((item) => item.item_id !== itemId),
            };
          })
          .filter((gift) => gift.items.length > 0);

        return { selectedFreeGift: updatedFreeGifts };
      }),
    totalDiscount: 0,
    setTotalDiscount: (total) => set({ totalDiscount: total }),
    clearSelectedPromotions: () =>
      set({ selectedPromotion: [], selectedFreeGift: [], totalDiscount: 0 }),
  }))
);
